import { z } from "zod";
import { parseJapaneseDate } from "../../utils/japaneseDate";
import {
  DATE_RANGE_KEYS,
  PARTIAL_TEXT_KEYS,
  V2_SEARCH_ARRAY_KEYS,
  V2_SEARCH_FLAG_KEYS,
  V2_SEARCH_TEXT_KEYS,
  constructionDates,
  digitsOnly,
  normalizeInput,
  parseNumberInput,
  type V2SearchConditions,
  type V2SearchTextKey,
} from "../../utils/v2SearchParams";

interface NumberRule {
  integer?: boolean;
  /** 所在階（地下は負）だけが負の値を受け付ける */
  allowNegative?: boolean;
}

export const NUMBER_RANGES: ReadonlyArray<
  readonly [V2SearchTextKey, V2SearchTextKey, NumberRule]
> = [
  ["landAreaMin", "landAreaMax", {}],
  ["floorAreaMin", "floorAreaMax", {}],
  ["ageMin", "ageMax", { integer: true }],
  ["floorsMin", "floorsMax", { integer: true }],
  ["oneBuildingFloorAreaMin", "oneBuildingFloorAreaMax", {}],
  ["unitFloorMin", "unitFloorMax", { integer: true, allowNegative: true }],
  ["annexFloorAreaMin", "annexFloorAreaMax", {}],
  ["ownerCountMin", "ownerCountMax", { integer: true }],
  ["amountMin", "amountMax", {}],
  ["rankMin", "rankMax", { integer: true }],
];

/** 単独では条件にならない切り替え（「過去の登記も含める」など） */
const MODIFIER_FLAGS = new Set<string>([
  "locationHistory",
  "ownerHistory",
  "restrictionsHistory",
  "securityHistory",
]);

const SHORT_TEXT_MESSAGE =
  "1〜2文字で検索するときは、ほかの条件も指定してください";

export const NUMBER_MIN_LENGTH_MESSAGE = "2文字以上で入力してください";
export const isInvalidPropertyNumber = (value: string): boolean =>
  [...normalizeInput(value)].length === 1;

const isShort = (value: string): boolean => {
  const length = [...value.trim()].length;
  return length > 0 && length < 3;
};

/** 1〜2文字の部分一致の文字列のほかに、条件が1つでもあるか */
const hasOtherCondition = (values: V2SearchConditions): boolean =>
  V2_SEARCH_TEXT_KEYS.some(
    (key) =>
      values[key].trim() !== "" &&
      !(
        (PARTIAL_TEXT_KEYS as readonly string[]).includes(key) &&
        isShort(values[key])
      )
  ) ||
  V2_SEARCH_ARRAY_KEYS.some((key) => values[key].length > 0) ||
  V2_SEARCH_FLAG_KEYS.some((key) => !MODIFIER_FLAGS.has(key) && values[key]);

const conditionsShape = Object.fromEntries([
  ...V2_SEARCH_TEXT_KEYS.map((key) => [key, z.string()]),
  ...V2_SEARCH_ARRAY_KEYS.map((key) => [key, z.array(z.string())]),
  ...V2_SEARCH_FLAG_KEYS.map((key) => [key, z.boolean()]),
]) as Record<string, z.ZodTypeAny>;

export const v2SearchConditionsSchema = z
  .object(conditionsShape)
  .superRefine((raw, ctx) => {
    const values = raw as V2SearchConditions;
    const issue = (key: V2SearchTextKey, message: string): void => {
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: [key], message });
    };

    NUMBER_RANGES.forEach(([minKey, maxKey, rule]) => {
      const parsed = [minKey, maxKey].map((key) => {
        const value = parseNumberInput(values[key]);
        if (value == null) return null;
        if (Number.isNaN(value)) {
          issue(key, "数値を入力してください");
          return null;
        }
        if (!rule.allowNegative && value < 0) {
          issue(key, "0以上を入力してください");
          return null;
        }
        if (rule.integer && !Number.isInteger(value)) {
          issue(key, "整数を入力してください");
          return null;
        }
        return value;
      });
      if (parsed[0] != null && parsed[1] != null && parsed[0] > parsed[1]) {
        issue(maxKey, "下限以上の値を入力してください");
      }
    });

    DATE_RANGE_KEYS.forEach(([fromKey, toKey]) => {
      const from = values[fromKey].trim()
        ? parseJapaneseDate(values[fromKey], "start")
        : null;
      const to = values[toKey].trim()
        ? parseJapaneseDate(values[toKey], "end")
        : null;
      if (values[fromKey].trim() && from == null)
        issue(
          fromKey,
          "日付を読めません（例: 2000/1/1、平成12年1月1日、H12.1.1）"
        );
      if (values[toKey].trim() && to == null)
        issue(
          toKey,
          "日付を読めません（例: 2000/1/1、平成12年1月1日、H12.1.1）"
        );
      if (from != null && to != null && from > to)
        issue(toKey, "開始日以降の日付を入力してください");
    });

    const constructionRange = constructionDates(values, new Date());
    if (
      (values.builtFrom.trim() || values.builtTo.trim()) &&
      (values.ageMin.trim() || values.ageMax.trim()) &&
      constructionRange?.from &&
      constructionRange.to &&
      constructionRange.from > constructionRange.to
    ) {
      issue("builtFrom", "新築年月日と築年数の範囲が重なりません");
    }

    const realEstateNumber = digitsOnly(values.realEstateNumber);
    if (realEstateNumber !== "" && !/^\d{13}$/.test(realEstateNumber)) {
      issue("realEstateNumber", "不動産番号は13桁の数字で入力してください");
    }
    const ownerNumber = digitsOnly(values.ownerNumber);
    if (ownerNumber !== "" && !/^(\d{12}|\d{13})$/.test(ownerNumber)) {
      issue(
        "ownerNumber",
        "法人番号（13桁）または会社法人等番号（12桁）を入力してください"
      );
    }
    const requestId = normalizeInput(values.requestId);
    if (requestId !== "" && !/^[1-9]\d*$/.test(requestId)) {
      issue("requestId", "解析依頼の ID は数字で入力してください");
    }

    if (isInvalidPropertyNumber(values.number)) {
      issue("number", NUMBER_MIN_LENGTH_MESSAGE);
    }

    if (!hasOtherCondition(values)) {
      PARTIAL_TEXT_KEYS.forEach((key) => {
        if (isShort(values[key])) issue(key, SHORT_TEXT_MESSAGE);
      });
    }
  });
